// src/features/workspace/SavedViewsBar.tsx
// The saved-views strip: one pill per view plus the built-in "all items" pill,
// and the save / save-as / reset actions for the view currently applied.
//
// Presentational only — it knows nothing about TaskView, the store or the
// workspace state. The container maps those onto `SavedViewItem`s and handles
// the writes, so this file stays reusable for any product that grows views.

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useI18n } from '@react/hooks';
import { Button } from '@react/components';
import { cn } from '@core/ui';

export interface SavedViewItem {
  id: string;
  name: string;
  isShared: boolean;
  isDefault: boolean;
  /** Whether the current user may overwrite, re-default or delete this view. */
  canEdit: boolean;
}

interface SavedViewsBarProps {
  views: SavedViewItem[];
  activeId: string | null;
  /** The active view no longer matches what the workspace shows. */
  isModified: boolean;
  isLoading: boolean;
  isSaving: boolean;
  error?: unknown;
  onSelect: (id: string | null) => void;
  onSaveCurrent: () => void;
  onSaveAs: () => void;
  onReset: () => void;
  onToggleDefault: (id: string) => void;
  onDelete: (id: string) => void;
  onRetry: () => void;
}

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------

function UsersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
    </svg>
  );
}

function StarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" stroke="none" aria-hidden="true">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}

function MoreIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" stroke="none" aria-hidden="true">
      <circle cx="12" cy="5" r="1.75" />
      <circle cx="12" cy="12" r="1.75" />
      <circle cx="12" cy="19" r="1.75" />
    </svg>
  );
}

function BookmarkPlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M19 21l-7-5-7 5V5a2 2 0 012-2h6" />
      <line x1="18" y1="3" x2="18" y2="9" />
      <line x1="15" y1="6" x2="21" y2="6" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Per-view action menu
// ---------------------------------------------------------------------------

interface ViewMenuProps {
  view: SavedViewItem;
  onToggleDefault: () => void;
  onDelete: () => void;
}

function ViewMenu({ view, onToggleDefault, onDelete }: ViewMenuProps) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<React.CSSProperties>({});

  const updatePosition = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setStyle({ position: 'fixed', top: rect.bottom + 4, left: Math.max(8, rect.right - 200), width: 200 });
  }, []);

  useEffect(() => {
    if (!open) return;
    updatePosition();

    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        !triggerRef.current?.contains(target) &&
        !menuRef.current?.contains(target)
      ) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open, updatePosition]);

  const item = 'flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-foreground transition-colors hover:bg-accent/50';

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('workspace.savedViews.actions', { name: view.name })}
        title={t('workspace.savedViews.actions', { name: view.name })}
        onClick={(event) => {
          event.stopPropagation();
          setOpen((prev) => !prev);
        }}
        className="-mr-1 rounded p-0.5 text-current opacity-70 transition-opacity hover:opacity-100"
      >
        <MoreIcon className="h-3.5 w-3.5" />
      </button>

      {open && createPortal(
        <div
          ref={menuRef}
          role="menu"
          aria-label={t('workspace.savedViews.actions', { name: view.name })}
          style={style}
          className="z-50 rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-lg"
        >
          <button
            type="button"
            role="menuitem"
            className={item}
            onClick={() => {
              setOpen(false);
              onToggleDefault();
            }}
          >
            <StarIcon className={cn('h-3.5 w-3.5', view.isDefault ? 'text-primary' : 'text-muted-foreground')} />
            {view.isDefault
              ? t('workspace.savedViews.unsetDefault')
              : t('workspace.savedViews.setDefault')}
          </button>
          <button
            type="button"
            role="menuitem"
            className={cn(item, 'text-destructive hover:bg-destructive/10')}
            onClick={() => {
              setOpen(false);
              onDelete();
            }}
          >
            {t('workspace.savedViews.delete')}
          </button>
        </div>,
        document.body,
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Strip
// ---------------------------------------------------------------------------

export function SavedViewsBar({
  views,
  activeId,
  isModified,
  isLoading,
  isSaving,
  error,
  onSelect,
  onSaveCurrent,
  onSaveAs,
  onReset,
  onToggleDefault,
  onDelete,
  onRetry,
}: SavedViewsBarProps) {
  const { t } = useI18n();

  const activeView = views.find((view) => view.id === activeId) ?? null;
  // An id in the URL that no longer resolves (deleted, unshared, another
  // tenant) must not leave the strip with nothing marked: fall back to "all".
  const allSelected = !activeView;

  const pill = (selected: boolean) =>
    cn(
      'flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors whitespace-nowrap',
      selected
        ? 'border-primary/40 bg-accent text-accent-foreground'
        : 'border-border text-muted-foreground hover:bg-accent/50 hover:text-foreground',
    );

  if (isLoading && views.length === 0) {
    return (
      <div className="flex items-center gap-2" aria-busy="true" aria-live="polite">
        <span className="sr-only">{t('common.loading')}</span>
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={index} className="h-6 w-24 animate-pulse rounded-full bg-muted" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label={t('workspace.savedViews.label')}>
        <button
          type="button"
          onClick={() => onSelect(null)}
          aria-current={allSelected ? 'true' : undefined}
          className={pill(allSelected)}
        >
          {t('workspace.savedViews.all')}
        </button>

        {views.map((view) => {
          const selected = view.id === activeId;
          return (
            <div key={view.id} className={pill(selected)}>
              <button
                type="button"
                onClick={() => onSelect(view.id)}
                aria-current={selected ? 'true' : undefined}
                className="flex items-center gap-1.5 text-current"
              >
                {view.isDefault && (
                  <StarIcon className="h-3 w-3 flex-shrink-0 text-primary" />
                )}
                {view.isShared && (
                  <UsersIcon className="h-3 w-3 flex-shrink-0 opacity-70" />
                )}
                <span className="max-w-[12rem] truncate">{view.name}</span>
                <span className="sr-only">
                  {view.isShared
                    ? t('workspace.savedViews.shared')
                    : t('workspace.savedViews.personal')}
                </span>
                {selected && isModified && (
                  <span
                    className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary"
                    title={t('workspace.savedViews.modified')}
                    aria-label={t('workspace.savedViews.modified')}
                  />
                )}
              </button>

              {selected && view.canEdit && (
                <ViewMenu
                  view={view}
                  onToggleDefault={() => onToggleDefault(view.id)}
                  onDelete={() => onDelete(view.id)}
                />
              )}
            </div>
          );
        })}

        {/* Actions for what is currently on screen */}
        <div className="ml-1 flex items-center gap-1">
          {activeView && isModified ? (
            <>
              {activeView.canEdit && (
                <Button size="sm" variant="secondary" onClick={onSaveCurrent} disabled={isSaving}>
                  {t('workspace.savedViews.save')}
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={onSaveAs} disabled={isSaving}>
                {t('workspace.savedViews.saveAs')}
              </Button>
              <Button size="sm" variant="ghost" onClick={onReset} disabled={isSaving}>
                {t('workspace.savedViews.reset')}
              </Button>
            </>
          ) : (
            <button
              type="button"
              onClick={onSaveAs}
              disabled={isSaving}
              className="flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent/50 hover:text-foreground disabled:opacity-50"
            >
              <BookmarkPlusIcon className="h-3.5 w-3.5" />
              {t('workspace.savedViews.saveCurrent')}
            </button>
          )}
        </div>
      </div>

      {views.length === 0 && !isLoading && !error && (
        <p className="text-xs text-muted-foreground">{t('workspace.savedViews.empty')}</p>
      )}

      {error ? (
        <p className="flex items-center gap-2 text-xs text-destructive">
          {t('workspace.savedViews.loadError')}
          <button type="button" onClick={onRetry} className="underline hover:no-underline">
            {t('common.retry')}
          </button>
        </p>
      ) : null}
    </div>
  );
}
